import React, { useEffect, useState } from 'react';

import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Checkbox,
  Drawer,
} from '@material-ui/core';

import { GetApp } from '@material-ui/icons';

import { primaryColor, theme } from 'theme/theme';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { stackActions } from 'actions/stackActions';
import IconSpinner from 'components/spinners/ButtonSpinner';
import { ListStacksResponseItem } from '.generated/api';

const useStyles = makeStyles({
  container: {
    overflow: 'auto',
    flex: 1,
  },
  pagination: {},
  tableHeadCell: {
    backgroundColor: 'rgba(237, 240, 242, 1)', // transparent background is no good for sticky header
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    position: 'relative',
    '& > a': {
      color: 'inherit',
    },
    paddingTop: 0,
    paddingBottom: 0,
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
    cursor: 'pointer',
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  measuredIcon: {
    position: 'absolute',
    right: '100%',
    fontSize: '1rem',
    marginRight: '-0.5rem',
  },
  spinner: {
    height: '22px',
    width: '22px',
  },
  dialogContent: {
    height: 'auto',
    maxHeight: '33vh',
  },
  drawer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'rgba(217, 220, 225, 1)',
  },
  checkbox: {
    padding: 0,
  },
  radio: {
    margin: 0,
  },
});

function lastChanged(stack: ListStacksResponseItem) {
  const lastModifiedDateTime = new Date(stack.lastModifiedDateTime);
  if (lastModifiedDateTime) {
    return lastModifiedDateTime.toLocaleDateString(navigator.language);
  }

  return '';
}

interface SawmillStacksTableProps {
  onPageChanged?: (page: number) => void;
  onPageSizeChanged?: (page: number) => void;
}

function SawmillStacksTable({
  onPageChanged,
  onPageSizeChanged,
}: SawmillStacksTableProps): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [selectedStackIds, setSelectedStackIds] = useState<string[]>([]);

  const { stacks, filters } = useSelector((state: ApplicationState) => ({
    stacks: state.stacks?.stacks,
    filters: state.stacks?.filters,
  }));

  const sawmillUserInfo = useSelector(
    (state: ApplicationState) => state.isLoggedIn.sawmillUserInfo,
  );

  const { archiveDownloadProgress } = useSelector(
    (state: ApplicationState) => ({
      stacksDeleting: state.stacks?.stacksDeleting,
      archiveDownloadProgress: state.stacks?.archiveDownloadProgress,
    }),
  );

  const [organizations, setOrganizations] = useState<
    Record<string, { name: string }>
  >({});
  const [departments, setDepartments] = useState<
    Record<string, { name: string; organizationId: string }>
  >({});

  // update organization/department maps
  useEffect(() => {
    const newOrganizations: Record<string, { name: string }> = {};
    const newDepartments: Record<
      string,
      { name: string; organizationId: string }
    > = {};

    if (sawmillUserInfo?.organizations) {
      for (const organization of sawmillUserInfo?.organizations) {
        newOrganizations[organization.organizationId] = {
          name: organization.organizationName,
        };
        for (const department of organization.departments) {
          newDepartments[department.departmentId] = {
            name: department.departmentName,
            organizationId: organization.organizationId,
          };
        }
      }
      setOrganizations(newOrganizations);
      setDepartments(newDepartments);
    } else {
      setOrganizations({});
      setDepartments({});
    }
  }, [sawmillUserInfo]);

  function handleDownloadClick() {
    dispatch(stackActions.downloadStackArchive(selectedStackIds));
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onPageChanged) {
      onPageChanged(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (onPageSizeChanged) {
      onPageSizeChanged(Number(event.target.value));
    }
  };

  // checkbox select logic
  const handleSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && stacks) {
      setSelectedStackIds(stacks.data.map((stack) => stack.stackId));
    } else {
      setSelectedStackIds([]);
    }
  };

  const handleSelectRowClick = (stackId: string) => {
    const selectedIndex = selectedStackIds.indexOf(stackId);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedStackIds, stackId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedStackIds.slice(1));
    } else if (selectedIndex === selectedStackIds.length - 1) {
      newSelected = newSelected.concat(selectedStackIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedStackIds.slice(0, selectedIndex),
        selectedStackIds.slice(selectedIndex + 1),
      );
    }

    setSelectedStackIds(newSelected);
  };

  const isSelected = (stackId: string) =>
    selectedStackIds.indexOf(stackId) !== -1;

  const numSelected = selectedStackIds.length;
  const rowCount = stacks?.data.length ?? 0;

  return (
    <>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                <Checkbox
                  className={classes.checkbox}
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.stack_no')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.stack_id')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.organization')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.department')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.surveyor')}
              </TableCell>
              <TableCell align="right" className={classes.tableHeadCell}>
                {t('stacks_page.table.last_changed')}
              </TableCell>
              <TableCell
                align="center"
                className={classes.tableHeadCell}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stacks?.data?.map((stack: ListStacksResponseItem) => {
              const department = departments[stack.departmentId];
              const organization = department
                ? organizations[department.organizationId]
                : undefined;

              return (
                <TableRow
                  key={stack.stackId}
                  hover={true}
                  className={classes.tableRow}
                  // onClick={
                  //   organization
                  //     ? () => {
                  //         history.push(
                  //           `/organization/${department.organizationId}/department/${stack.departmentId}/stack/${stack.stackId}`,
                  //         );
                  //       }
                  //     : undefined
                  // }
                >
                  <TableCell className={classes.textBold}>
                    <Checkbox
                      className={classes.checkbox}
                      checked={isSelected(stack.stackId)}
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        handleSelectRowClick(stack.stackId);
                      }}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {stack.stackNumber}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {stack.polterId ?? ''}
                  </TableCell>
                  <TableCell className={classes.textBold}>
                    {organization && organization.name}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {department && department.name}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {stack.surveyor}
                  </TableCell>
                  <TableCell align="right" className={classes.tableCell}>
                    {lastChanged(stack)}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        className={classes.pagination}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={stacks?.totalCount ?? 0}
        rowsPerPage={filters?.pageSize ?? 50}
        page={stacks?.page ? stacks?.page - 1 : 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Drawer
        variant="persistent"
        anchor="bottom"
        open={selectedStackIds.length > 0}
      >
        <div className={classes.drawer}>
          <Button
            disabled={archiveDownloadProgress !== undefined}
            onClick={handleDownloadClick}
            color="secondary"
            startIcon={
              <IconSpinner
                isLoading={!!archiveDownloadProgress}
                progress={archiveDownloadProgress}
              >
                <GetApp />
              </IconSpinner>
            }
          >
            {t('common.download')}
          </Button>
        </div>
      </Drawer>
    </>
  );
}

export { SawmillStacksTable };
